import { Controller } from "@hotwired/stimulus"
import { toast } from "../utils/toast"
import RecordRTC from "recordrtc"
import lamejs from "lamejs"

export default class extends Controller {
  static targets = ["recordButton", "recordingStatus", "recordingDuration", "audioDuration", "microphoneIcon", "pauseIcon", "stopIcon", "buttonText", "loadingDots", "statusText", "audioContainer", "audioButton", "deleteButton"]

  connect() {
    this.audioRecorder = null
    this.audioStream = null
    this.recordedAudioBlob = null
    this.isRecording = false
    this.isPaused = false
    this.recordingStartTime = null
    this.pausedDuration = 0
    this.pauseStartTime = null
    this.recordingTimer = null

    // Check audio support on load
    setTimeout(() => {
      const supportError = this.checkAudioSupport()
      if (supportError) {
        console.log('Audio recording not available:', supportError)
        this.disableAudioRecording()
      }
    }, 1000)
  }

  checkAudioSupport() {
    // Check if we're in a secure context (allow amberjack dev machine)
    const isDevMachine = window.location.hostname === 'amberjack'
    if (!window.isSecureContext && !isDevMachine) {
      return 'Audio recording requires HTTPS or localhost'
    }

    // Check for MediaDevices API
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return 'Audio recording is not supported in this browser'
    }

    // Check for RecordRTC
    if (!RecordRTC) {
      return 'Audio recording library failed to load'
    }

    // Check for lamejs (MP3 encoder)
    if (!lamejs) {
      return 'MP3 encoding library failed to load'
    }

    return null // No errors
  }

  async initializeAudioRecording() {
    // Check for browser support first
    const supportError = this.checkAudioSupport()
    if (supportError) {
      console.error('Audio support check failed:', supportError)
      toast.warning(supportError + '. Audio recording will be disabled.')
      this.disableAudioRecording()
      return false
    }

    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      })

      this.audioRecorder = new RecordRTC(this.audioStream, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 44100,
        timeSlice: 1000
      })

      return true
    } catch (error) {
      console.error('Error accessing microphone:', error)
      let errorMessage = 'Unable to access microphone. '

      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow microphone access and try again.'
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No microphone found.'
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Audio recording is not supported.'
      } else {
        errorMessage += 'Please check your permissions and try again.'
      }

      toast.error(errorMessage)
      this.disableAudioRecording()
      return false
    }
  }

  disableAudioRecording() {
    this.element.innerHTML = `
      <div class="alert alert-warning">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Audio recording is not available in this browser or requires HTTPS.</span>
      </div>
    `
  }

  setUiStatusActive() {
    // Update UI - show pause icon, hide microphone icon
    if (this.hasStopIconTarget) {
      this.stopIconTarget.classList.add('hidden')
    }
    if (this.hasMicrophoneIconTarget) {
      this.microphoneIconTarget.classList.add('hidden')
    }
    if (this.hasPauseIconTarget) {
      this.pauseIconTarget.classList.remove('hidden')
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = 'Pause Recording'
    }
    if (this.hasRecordingStatusTarget) {
      this.recordingStatusTarget.classList.remove('hidden')
    }
    if (this.hasLoadingDotsTarget) {
      this.loadingDotsTarget.classList.remove('hidden')
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = 'Recording...'
    }
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.add('hidden')
    }

    // Camera-style UI: expand container to show duration
    if (this.hasAudioContainerTarget) {
      this.audioContainerTarget.classList.remove('w-14', 'bg-indigo-600')
      this.audioContainerTarget.classList.add('w-[136px]', 'bg-white')
    }
    if (this.hasAudioButtonTarget) {
      this.audioButtonTarget.classList.remove('bg-transparent')
      this.audioButtonTarget.classList.add('bg-indigo-600')
    }
    if (this.hasAudioDurationTarget) {
      this.audioDurationTarget.classList.remove('w-0', 'opacity-0')
      this.audioDurationTarget.classList.add('w-20', 'opacity-100')
    }
  }

  setUiStatusPaused() {
    // Update UI - show microphone icon, hide pause icon
    if (this.hasPauseIconTarget) {
      this.pauseIconTarget.classList.add('hidden')
    }
    if (this.hasMicrophoneIconTarget) {
      this.microphoneIconTarget.classList.remove('hidden')
    }
    if (this.hasStopIconTarget) {
      this.stopIconTarget.classList.add('hidden')
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = 'Resume Recording'
    }
    if (this.hasDeleteButtonTarget) {
      this.deleteButtonTarget.classList.remove('hidden')
    }

    // Update recording status to show "Paused" without loading dots
    if (this.hasLoadingDotsTarget) {
      this.loadingDotsTarget.classList.add('hidden')
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = 'Paused'
    }
  }

  async toggleRecording() {
    if (!this.isRecording) {
      // Start recording
      await this.startRecording()
    } else if (this.isPaused) {
      // Resume recording
      this.resumeRecording()
    } else {
      // Pause recording
      this.pauseRecording()
    }
  }

  async startRecording() {
    if (!this.audioRecorder) {
      const initialized = await this.initializeAudioRecording()
      if (!initialized) return
    }

    this.recordingStartTime = Date.now()
    this.pausedDuration = 0
    this.pauseStartTime = null
    this.audioRecorder.startRecording()
    this.isRecording = true
    this.isPaused = false

    this.setUiStatusActive()

    // Start timer
    this.recordingTimer = setInterval(() => this.updateRecordingDuration(), 1000)
  }

  pauseRecording() {
    if (this.audioRecorder && this.isRecording && !this.isPaused) {
      this.audioRecorder.pauseRecording()
      this.isPaused = true
      this.pauseStartTime = Date.now()

      // Stop the timer
      clearInterval(this.recordingTimer)
      this.setUiStatusPaused()
    }
  }

  resumeRecording() {
    if (this.audioRecorder && this.isRecording && this.isPaused) {
      this.audioRecorder.resumeRecording()
      this.isPaused = false

      // Add the paused duration to total
      this.pausedDuration += Date.now() - this.pauseStartTime
      this.pauseStartTime = null

      // Restart the timer
      this.recordingTimer = setInterval(() => this.updateRecordingDuration(), 1000)

      this.setUiStatusActive()
    }
  }

  removeAudio() {
    this.recordedAudioBlob = null

    // Reset recording state
    this.isRecording = false
    this.isPaused = false
    this.recordingStartTime = null
    this.pausedDuration = 0
    this.pauseStartTime = null

    // Reset button UI
    if (this.hasMicrophoneIconTarget) {
      this.microphoneIconTarget.classList.remove('hidden')
    }
    if (this.hasPauseIconTarget) {
      this.pauseIconTarget.classList.add('hidden')
    }
    if (this.hasStopIconTarget) {
      this.stopIconTarget.classList.add('hidden')
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = 'Start Recording'
    }
    if (this.hasRecordingStatusTarget) {
      this.recordingStatusTarget.classList.add('hidden')
    }

    // Reset audio input
    const audioInput = document.getElementById('audio-input')
    if (audioInput) {
      audioInput.value = ''
    }
  }

  updateRecordingDuration() {
    if (this.recordingStartTime) {
      // Calculate elapsed time minus any paused duration
      const elapsed = Math.round((Date.now() - this.recordingStartTime - this.pausedDuration) / 1000)
      const formattedDuration = this.formatDuration(elapsed)

      if (this.hasRecordingDurationTarget) {
        this.recordingDurationTarget.textContent = formattedDuration
      }

      // Camera-style UI: update inline duration display
      if (this.hasAudioDurationTarget) {
        const durationSpan = this.audioDurationTarget.querySelector('span')
        if (durationSpan) {
          durationSpan.textContent = formattedDuration
        }
      }
    }
  }

  formatDuration(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Called by form submission to ensure recording is stopped and audio is ready
  async finalizeRecording() {
    if (this.isRecording) {
      console.log('Finalizing active audio recording...')
      // Stop the recording and wait for it to complete
      return new Promise((resolve) => {
        this.audioRecorder.stopRecording(() => {
          // Run the original stop logic
          const wavBlob = this.audioRecorder.getBlob()
          this.recordedAudioBlob = wavBlob

          // Stop audio stream
          if (this.audioStream) {
            this.audioStream.getTracks().forEach(track => track.stop())
            this.audioStream = null
          }

          // Reset state
          this.isRecording = false
          this.isPaused = false

          // Clear timer
          clearInterval(this.recordingTimer)

          resolve(this.recordedAudioBlob)
        })
      })
    }

    // If not recording, just return the existing blob
    return this.recordedAudioBlob
  }
}